import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { TrustBadges } from "./TrustBadges";
import { sendEmail } from "@/utils/email";

interface ApplyNowModalProps {
  children: React.ReactNode;
  loanType?: string;
  offer?: {
    title: string;
    code: string;
  };
}

export const ApplyNowModal = ({ children, loanType, offer }: ApplyNowModalProps) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    loanAmount: "",
    loanType: loanType || "",
    tenure: "",
    offerTitle: offer?.title || "",
    offerCode: offer?.code || ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.phone) {
      toast({
        title: "Error",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    // Prepare form data for email
    const emailData = {
      from_name: formData.name,
      from_email: formData.email,
      to_email: import.meta.env.VITE_EMAILJS_BUSINESS_EMAIL || 'info@whitepearlfinance.com',
      phone: formData.phone,
      loan_type: formData.loanType,
      message: `New loan application received:

👤 Name: ${formData.name}
📧 Email: ${formData.email}
📞 Phone: ${formData.phone}
🏦 Loan Type: ${formData.loanType}
💰 Loan Amount: ${formData.loanAmount}
⏳ Preferred Tenure: ${formData.tenure}${formData.offerTitle ? `
• Offer: ${formData.offerTitle}
• Code: ${formData.offerCode}` : ''}

---
📅 Submitted on: ${new Date().toLocaleString()}
🌐 This is an automated message from the website contact form.`
    };

    try {
      const { success, message } = await sendEmail(emailData);

      if (success) {
        // Reset form on success
        setFormData({
          name: "",
          phone: "",
          email: "",
          loanAmount: "",
          loanType: loanType || "",
          tenure: "",
          offerTitle: offer?.title || "",
          offerCode: offer?.code || ""
        });

        // Close the modal after successful submission
        const dialog = document.querySelector('[role="dialog"]');
        if (dialog) {
          const closeButton = dialog.querySelector('button[aria-label="Close"]') as HTMLButtonElement;
          if (closeButton) closeButton.click();
        }
      }

      // Show success/error message
      toast({
        title: success ? "🎉 Application Submitted!" : "❌ Error",
        description: success
          ? "Thank you for your application! Our team will contact you shortly with the next steps."
          : message || "Failed to submit your application. Please try again.",
        variant: success ? "default" : "destructive"
      });
    } catch (error) {
      console.error("Error submitting application:", error);
      toast({
        title: "❌ Error",
        description: "An unexpected error occurred. Please try again later.",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="bg-slate-900/95 backdrop-blur-lg border-blue-500/30 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl text-center bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            {offer?.title || (loanType ? `Apply for ${loanType}` : 'Apply for Instant Loan Approval')}
          </DialogTitle>
          {offer?.code && (
            <p className="text-center text-blue-300">
              Offer Code: <span className="font-mono bg-blue-500/20 px-2 py-1 rounded">{offer.code}</span>
            </p>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white">Full Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                placeholder="Enter your full name"
                required
              />
            </div>

            <div>
              <Label className="text-white">Phone Number *</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                placeholder="Enter 10-digit mobile number"
                required
              />
            </div>

            <div>
              <Label className="text-white">Email Address *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                placeholder="Enter your email"
                required
              />
            </div>

            <div>
              <Label className="text-white">Loan Amount *</Label>
              <Input
                value={formData.loanAmount}
                onChange={(e) => setFormData({...formData, loanAmount: e.target.value})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                placeholder="Enter amount (₹)"
                required
              />
            </div>

            {!loanType && (
              <div>
                <Label className="text-white">Loan Type *</Label>
                <Select onValueChange={(value) => setFormData({...formData, loanType: value})}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue placeholder="Select loan type" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600 max-h-[300px] overflow-y-auto z-[100] relative">
                    <SelectItem value="home">Home Loan</SelectItem>
                    <SelectItem value="business">Business Loan</SelectItem>
                    <SelectItem value="mortgage">Mortgage Loan</SelectItem>
                    <SelectItem value="project">Project Loan</SelectItem>
                    <SelectItem value="personal">Personal Loan</SelectItem>
                    <SelectItem value="education">Education Loan</SelectItem>
                    <SelectItem value="new-car">New Car Loan</SelectItem>
                    <SelectItem value="used-car">Used Car Loan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-white">Tenure (Months) *</Label>
              <Select onValueChange={(value) => setFormData({...formData, tenure: value})}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Select tenure" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="6">6 Months</SelectItem>
                  <SelectItem value="12">12 Months</SelectItem>
                  <SelectItem value="24">24 Months</SelectItem>
                  <SelectItem value="36">36 Months</SelectItem>
                  <SelectItem value="48">48 Months</SelectItem>
                  <SelectItem value="60">60 Months</SelectItem>
                  <SelectItem value="84">84 Months</SelectItem>
                  <SelectItem value="120">120 Months</SelectItem>
                  <SelectItem value="240">240 Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              type="submit"
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 h-12 text-lg"
            >
              Get Instant Approval ⚡
            </Button>
          </div>
        </form>

        <TrustBadges />

        <div className="text-center text-gray-400 text-sm">
          <p>💬 Call us: <a href="tel:+919324471736" className="text-blue-400 hover:underline">+91 93244 71736</a></p>
          <p>⚡ Get approved in 48 hours with minimal documentation</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
